import React, { useMemo, useState } from 'react';
import {
  Typography,
  Button,
  Space,
  Table,
  Tag,
  Input,
  Select,
  Avatar,
  Empty,
  message,
  Segmented,
  Drawer,
} from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faMagnifyingGlass,
  faFilter,
  faUser,
  faArrowsRotate,
  faTableList,
  faTableColumns,
} from '@fortawesome/pro-solid-svg-icons';
import type { ColumnsType } from 'antd/es/table';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getIssues, updateIssue, deleteIssue, syncBugReports, IssueWithRelations } from '../../services/issues';
import { useAuthStore } from '../../store/authStore';
import { getUsers } from '../../services/users';
import CreateIssueModal from './CreateIssueModal';
import IssueDetail from './IssueDetail';
import { issueReference } from '../../utils/publicReferences';
import { getIssueDisplayTitle } from './issueDisplay';
import { colors } from '../../theme/colors';
import { useSearchParams } from 'react-router-dom';
import './Queue.css';

const { Title } = Typography;
const { Option } = Select;
const { Search } = Input;

const storyPointsForPriority = (priority: string) => ({
  LOWEST: 1,
  LOW: 2,
  MEDIUM: 3,
  HIGH: 5,
  HIGHEST: 8,
}[priority] || 3);

const priorityRank: Record<string, number> = {
  LOWEST: 1,
  LOW: 2,
  MEDIUM: 3,
  HIGH: 4,
  HIGHEST: 5,
};

const statusRank: Record<string, number> = {
  BACKLOG: 1,
  TODO: 2,
  IN_PROGRESS: 3,
  REVIEW: 4,
  TESTING: 5,
  DONE: 6,
};

const OpsQueue: React.FC = () => {
  const { user } = useAuthStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [priorityFilter, setPriorityFilter] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'list' | 'board'>('list');
  const [quickFilter, setQuickFilter] = useState<'all' | 'mine' | 'unassigned' | 'backlog' | 'high' | 'reports'>('all');
  const [assigneeFilter, setAssigneeFilter] = useState<string>('all');
  const [projectFilter, setProjectFilter] = useState<string>('all');
  const [issueFooterNode, setIssueFooterNode] = useState<HTMLDivElement | null>(null);
  const [selectedIssueId, setSelectedIssueId] = useState<string | null>(() => searchParams.get('issue'));
  const [dragOverStatus, setDragOverStatus] = useState<string | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [createIssueStatus, setCreateIssueStatus] = useState<string>('BACKLOG');
  const [isSyncing, setIsSyncing] = useState(false);

  React.useEffect(() => {
    setSelectedIssueId(searchParams.get('issue'));
  }, [searchParams]);

  const openIssueInspector = (issueId: string) => {
    setSelectedIssueId(issueId);
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.set('issue', issueId);
      return next;
    }, { replace: true });
  };

  const closeIssueInspector = () => {
    setSelectedIssueId(null);
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.delete('issue');
      return next;
    }, { replace: true });
  };

  const replaceIssueReference = React.useCallback((issueId: string) => {
    setSelectedIssueId(issueId);
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.set('issue', issueId);
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // Build filters for API
  const apiFilters: any = {};
  if (statusFilter !== 'all') apiFilters.status = statusFilter;
  if (typeFilter !== 'all') apiFilters.type = typeFilter;
  if (priorityFilter !== 'all') apiFilters.priority = priorityFilter;

  const { data: issues = [], isLoading } = useQuery({
    queryKey: ['issues', apiFilters],
    queryFn: () => getIssues(apiFilters),
  });

  const { data: users = [] } = useQuery({
    queryKey: ['users'],
    queryFn: getUsers,
  });

  const handleSyncReports = async () => {
    try {
      setIsSyncing(true);
      const result = await syncBugReports();
      const { summary } = result;
      const sourceSummary = summary.sources
        ? ` (${summary.sources.production} production, ${summary.sources.development} development)`
        : '';
      if (summary.errors > 0) {
        message.warning(
          `Processed ${summary.total} reports${sourceSummary}: ${summary.created} created, ${summary.alreadyProcessed} already synced, ${summary.errors} failed`
        );
      } else {
        message.success(
          `Processed ${summary.total} reports${sourceSummary}: ${summary.created} created, ${summary.alreadyProcessed} already synced`
        );
      }
      queryClient.invalidateQueries({ queryKey: ['issues'] });
    } catch (error: any) {
      const timeoutMessage = error.code === 'ECONNABORTED'
        ? 'Sync reports timed out. The backend may still be processing; refresh and try again in a minute.'
        : 'Failed to sync reports';
      message.error(error.response?.data?.error || timeoutMessage);
    } finally {
      setIsSyncing(false);
    }
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      BACKLOG: 'default',
      TODO: 'processing',
      IN_PROGRESS: 'warning',
      REVIEW: 'cyan',
      TESTING: 'blue',
      DONE: 'success',
    };
    return colors[status] || 'default';
  };

  const getTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      BUG: 'red',
      FEATURE: 'blue',
      TASK: 'green',
      EPIC: 'purple',
      STORY: 'orange',
    };
    return colors[type] || 'default';
  };

  const getPriorityColor = (priority: string) => {
    const colors: Record<string, string> = {
      LOWEST: 'default',
      LOW: 'cyan',
      MEDIUM: 'blue',
      HIGH: 'orange',
      HIGHEST: 'red',
    };
    return colors[priority] || 'default';
  };

  const filteredIssues = issues.filter((issue) => {
      const matchesSearch = searchText === '' ||
        issue.title.toLowerCase().includes(searchText.toLowerCase()) ||
        issue.description?.toLowerCase().includes(searchText.toLowerCase());
      const matchesAssignee = assigneeFilter === 'all' ||
        (assigneeFilter === 'unassigned' ? !issue.assignee?.id : issue.assignee?.id === assigneeFilter);
      const matchesProject = projectFilter === 'all' || issue.project?.id === projectFilter;
      const matchesQuickFilter =
        quickFilter === 'all' ||
        (quickFilter === 'mine' && issue.assignee?.id === user?.id) ||
        (quickFilter === 'unassigned' && !issue.assignee?.id) ||
        (quickFilter === 'backlog' && issue.status === 'BACKLOG') ||
        (quickFilter === 'high' && ['HIGH', 'HIGHEST'].includes(issue.priority)) ||
        (quickFilter === 'reports' && issue.labels?.includes('user-report'));
      return matchesSearch && matchesAssignee && matchesProject && matchesQuickFilter;
    });

  const projectOptions = Array.from(
    new Map(issues.filter((issue) => issue.project).map((issue) => [issue.project!.id, issue.project!])).values()
  );

  const hasActiveFilters = Boolean(
    searchText || statusFilter !== 'all' || typeFilter !== 'all' || priorityFilter !== 'all' ||
    assigneeFilter !== 'all' || projectFilter !== 'all' || quickFilter !== 'all'
  );

  const clearFilters = () => {
    setSearchText('');
    setStatusFilter('all');
    setTypeFilter('all');
    setPriorityFilter('all');
    setAssigneeFilter('all');
    setProjectFilter('all');
    setQuickFilter('all');
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- used for delete action in table
  const deleteIssueMutation = useMutation({
    mutationFn: deleteIssue,
    onSuccess: () => {
      message.success('Issue deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['issues'] });
    },
    onError: (error: any) => {
      message.error(error.response?.data?.error || 'Failed to delete issue');
    },
  });

  const updateIssueMutation = useMutation({
    mutationFn: ({ issueId, data }: { issueId: string; data: Record<string, any> }) =>
      updateIssue(issueId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['issues'] });
      queryClient.invalidateQueries({ queryKey: ['issue'] });
      message.success('Issue updated');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.error || 'Failed to update issue');
    },
  });

  const statusOptions = useMemo(
    () => ['BACKLOG', 'TODO', 'IN_PROGRESS', 'REVIEW', 'TESTING', 'DONE'],
    []
  );
  const priorityOptions = useMemo(
    () => ['LOWEST', 'LOW', 'MEDIUM', 'HIGH', 'HIGHEST'],
    []
  );

  const canEditIssue = (record: IssueWithRelations) => {
    if (!user) return false;
    if (user.role === 'ADMIN') return true;
    if (user.role === 'VIEWER') return false;
    return record.reporter?.id === user.id || record.assignee?.id === user.id;
  };

  const handleInlineUpdate = (issueId: string, data: Record<string, any>) => {
    updateIssueMutation.mutate({ issueId, data });
  };

  const columns: ColumnsType<IssueWithRelations> = [
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: 100,
      render: (type: string) => (
        <Tag color={getTypeColor(type)}>{type}</Tag>
      ),
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
      sorter: (a, b) => getIssueDisplayTitle(a.title, a.description)
        .localeCompare(getIssueDisplayTitle(b.title, b.description)),
      onCell: () => ({ style: { paddingInline: 16 } }),
      render: (title: string, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              openIssueInspector(issueReference(record));
            }}
            style={{ appearance: 'none', border: 0, padding: 0, background: 'transparent', color: '#F2F2F2', cursor: 'pointer', fontWeight: 600, textAlign: 'left' }}
          >
            {getIssueDisplayTitle(title, record.description)}
          </button>
          {record.labels && record.labels.length > 0 && (
            <Space size="small">
              {record.labels.map((label: string, idx: number) => (
                <Tag key={idx}>{label}</Tag>
              ))}
            </Space>
          )}
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 160,
      sorter: (a, b) => (statusRank[a.status] || 0) - (statusRank[b.status] || 0),
      render: (status: string, record) => {
        const editable = canEditIssue(record);
        if (!editable) {
          return <Tag color={getStatusColor(status)}>{status.replace('_', ' ')}</Tag>;
        }
        return (
          <div onClick={(event) => event.stopPropagation()}>
          <Select
            className="assignee-search-select"
            size="small"
            value={status}
            style={{ minWidth: 140 }}
            onChange={(value) => handleInlineUpdate(record.id, { status: value })}
            disabled={updateIssueMutation.isPending}
          >
            {statusOptions.map((option) => (
              <Option key={option} value={option}>
                {option.replace('_', ' ')}
              </Option>
            ))}
          </Select>
          </div>
        );
      },
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      width: 140,
      sorter: (a, b) => (priorityRank[a.priority] || 0) - (priorityRank[b.priority] || 0),
      render: (priority: string, record) => {
        const editable = canEditIssue(record);
        if (!editable) {
          return <Tag color={getPriorityColor(priority)}>{priority}</Tag>;
        }
        return (
          <div onClick={(event) => event.stopPropagation()}>
          <Select
            size="small"
            value={priority}
            style={{ minWidth: 120 }}
            onChange={(value) => handleInlineUpdate(record.id, { priority: value })}
            disabled={updateIssueMutation.isPending}
          >
            {priorityOptions.map((option) => (
              <Option key={option} value={option}>
                {option}
              </Option>
            ))}
          </Select>
          </div>
        );
      },
    },
    {
      title: 'Assignee',
      dataIndex: 'assigneeId',
      key: 'assigneeId',
      width: 200,
      sorter: (a, b) => {
        const aName = a.assignee?.name || a.assignee?.email || '';
        const bName = b.assignee?.name || b.assignee?.email || '';
        return aName.localeCompare(bName);
      },
      render: (_assigneeId: string, record) => {
        const editable = canEditIssue(record);
        if (!editable) {
          return record.assignee ? (
            <Space>
          <Avatar size="small" src={record.assignee.photoUrl} icon={<FontAwesomeIcon icon={faUser} />} />
              <span>{record.assignee.name || record.assignee.email}</span>
            </Space>
        ) : (
          <Tag>Unassigned</Tag>
          );
        }

        return (
          <div onClick={(event) => event.stopPropagation()}>
          <Select
            size="small"
            value={record.assignee?.id || 'unassigned'}
            style={{ minWidth: 180 }}
            showSearch
            optionFilterProp="label"
            onChange={(value) =>
              handleInlineUpdate(record.id, { assigneeId: value === 'unassigned' ? null : value })
            }
            disabled={updateIssueMutation.isPending}
          >
            <Option value="unassigned" label="Unassigned">
              <Tag>Unassigned</Tag>
            </Option>
            {users.map((teamMember) => (
              <Option
                key={teamMember.id}
                value={teamMember.id}
                label={teamMember.name || teamMember.email}
              >
                <Space>
                  <Avatar size="small" src={teamMember.photoUrl} icon={<FontAwesomeIcon icon={faUser} />} />
                  <span>{teamMember.name || teamMember.email}</span>
                </Space>
              </Option>
            ))}
          </Select>
          </div>
        );
      },
    },
    {
      title: 'Reporter',
      dataIndex: 'reporterId',
      key: 'reporterId',
      width: 120,
      render: (_reporterId: string, record) => (
        <Space size={6} title={record.reporter?.name || record.reporter?.email}>
          <Avatar size="small" src={record.reporter?.photoUrl} icon={<FontAwesomeIcon icon={faUser} />} />
          <span style={{ color: colors.textMuted, fontSize: 12 }}>{record.reporter?.name || 'Unknown'}</span>
        </Space>
      ),
    },
    {
      title: 'Points',
      dataIndex: 'storyPoints',
      key: 'storyPoints',
      width: 100,
      render: (_points: number, record) => (
        <span title="Automatically assigned from priority" style={{ color: colors.textPrimary, fontWeight: 600 }}>
          {storyPointsForPriority(record.priority)}
        </span>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      defaultSortOrder: 'descend',
      sorter: (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
  ];

  const boardColumns = [
    { key: 'BACKLOG', title: 'Backlog', status: 'BACKLOG' },
    { key: 'TODO', title: 'To Do', status: 'TODO' },
    { key: 'IN_PROGRESS', title: 'In Progress', status: 'IN_PROGRESS' },
    { key: 'REVIEW', title: 'Review', status: 'REVIEW' },
    { key: 'TESTING', title: 'Testing', status: 'TESTING' },
    { key: 'DONE', title: 'Done', status: 'DONE' },
  ];

  const openIssueCount = filteredIssues.filter((issue) => issue.status !== 'DONE').length;
  const unassignedIssueCount = filteredIssues.filter((issue) => !issue.assignee?.id).length;
  const highPriorityCount = filteredIssues.filter((issue) => ['HIGH', 'HIGHEST'].includes(issue.priority)).length;
  const totalStoryPoints = filteredIssues.reduce((total, issue) => total + storyPointsForPriority(issue.priority), 0);

  const handleBoardDrop = (issueId: string, status: string) => {
    const issue = issues.find((item) => item.id === issueId);
    setDragOverStatus(null);
    if (!issue || issue.status === status || !canEditIssue(issue)) return;
    handleInlineUpdate(issueId, { status });
  };

  return (
    <div className="queue-page" style={{ background: colors.bgBase }}>
      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={2} style={{ color: '#F2F2F2', margin: 0 }}>
          Ops Queue
        </Title>
        <Space>
          {user?.role === 'ADMIN' && (
            <Button
              icon={<FontAwesomeIcon icon={faArrowsRotate} spin={isSyncing} />}
              onClick={handleSyncReports}
              loading={isSyncing}
              style={{ background: '#1a1a1a', borderColor: '#333', color: '#F2F2F2' }}
            >
              Sync Reports
            </Button>
          )}
          <Button
            type="primary"
            icon={<FontAwesomeIcon icon={faPlus} />}
            style={{ background: colors.primary, borderColor: colors.primary }}
            onClick={() => {
              setCreateIssueStatus('BACKLOG');
              setIsCreateModalOpen(true);
            }}
          >
            New Ticket
          </Button>
        </Space>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
          borderTop: `1px solid ${colors.border}`,
          borderBottom: `1px solid ${colors.border}`,
          marginBottom: 18,
        }}
      >
        {[
          { label: 'Visible', value: filteredIssues.length, color: colors.textPrimary },
          { label: 'Open', value: openIssueCount, color: '#F29A8F' },
          { label: 'Unassigned', value: unassignedIssueCount, color: '#F2D788' },
          { label: 'High priority', value: highPriorityCount, color: '#EC8552' },
          { label: 'Story points', value: totalStoryPoints, color: '#F8B9E3' },
        ].map((metric, index) => (
          <div
            key={metric.label}
            style={{
              padding: '14px 18px',
              borderRight: index < 4 ? `1px solid ${colors.border}` : undefined,
            }}
          >
            <div style={{ color: metric.color, fontSize: 20, fontWeight: 700 }}>{metric.value}</div>
            <div style={{ color: colors.textMuted, fontSize: 12, marginTop: 2 }}>{metric.label}</div>
          </div>
        ))}
      </div>

      <div style={{ padding: '10px 0 14px', marginBottom: 10, position: 'sticky', top: 0, zIndex: 20, background: colors.bgBase }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 10 }}>
          <Space wrap size={[4, 4]}>
            {[
              { key: 'all', label: 'All' },
              { key: 'mine', label: 'Mine' },
              { key: 'unassigned', label: 'Unassigned' },
              { key: 'backlog', label: 'Backlog' },
              { key: 'high', label: 'High priority' },
              { key: 'reports', label: 'User reports' },
            ].map((filter) => (
              <Button
                key={filter.key}
                type="text"
                size="small"
                onClick={() => setQuickFilter(filter.key as typeof quickFilter)}
                style={{
                  color: quickFilter === filter.key ? colors.textPrimary : colors.textMuted,
                  background: quickFilter === filter.key ? colors.bgRaised : 'transparent',
                }}
              >
                {filter.label}
              </Button>
            ))}
          </Space>
          <Space>
            <Segmented
              className="planner-view-switch"
              aria-label="Queue view"
              value={viewMode}
              onChange={(value) => setViewMode(value as 'list' | 'board')}
              options={[
                { value: 'list', label: <FontAwesomeIcon icon={faTableList} aria-label="List" /> },
                { value: 'board', label: <FontAwesomeIcon icon={faTableColumns} aria-label="Board" /> },
              ]}
            />
          </Space>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
          <Search
            placeholder="Search issues..."
            allowClear
            prefix={<FontAwesomeIcon icon={faMagnifyingGlass} />}
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
            style={{ width: 260 }}
          />
          <Select value={statusFilter} onChange={setStatusFilter} style={{ width: 145 }}>
            <Option value="all">All statuses</Option>
            {statusOptions.map((status) => <Option key={status} value={status}>{status.replace('_', ' ')}</Option>)}
          </Select>
          <Select value={typeFilter} onChange={setTypeFilter} style={{ width: 135 }}>
            <Option value="all">All types</Option>
            {['BUG', 'FEATURE', 'TASK', 'EPIC', 'STORY'].map((type) => <Option key={type} value={type}>{type}</Option>)}
          </Select>
          <Select value={priorityFilter} onChange={setPriorityFilter} style={{ width: 145 }}>
            <Option value="all">All priorities</Option>
            {priorityOptions.map((priority) => <Option key={priority} value={priority}>{priority}</Option>)}
          </Select>
          <Select value={assigneeFilter} onChange={setAssigneeFilter} style={{ width: 170 }} showSearch optionFilterProp="label">
            <Option value="all" label="All assignees">All assignees</Option>
            <Option value="unassigned" label="Unassigned">Unassigned</Option>
            {users.map((teamMember) => (
              <Option key={teamMember.id} value={teamMember.id} label={teamMember.name || teamMember.email}>
                {teamMember.name || teamMember.email}
              </Option>
            ))}
          </Select>
          <Select value={projectFilter} onChange={setProjectFilter} style={{ width: 170 }} showSearch optionFilterProp="label">
            <Option value="all" label="All projects">All projects</Option>
            {projectOptions.map((project) => <Option key={project.id} value={project.id} label={project.name}>{project.name}</Option>)}
          </Select>
          {hasActiveFilters && (
            <Button type="text" icon={<FontAwesomeIcon icon={faFilter} />} onClick={clearFilters}>Clear</Button>
          )}
        </div>
      </div>

      {viewMode === 'list' ? (
        <div className="queue-table-region" role="region" aria-label="Ops Queue tickets" tabIndex={0}>
          <Table
            className="queue-list-table"
            columns={columns}
            dataSource={filteredIssues}
            rowKey="id"
            loading={isLoading}
            pagination={{ pageSize: 20 }}
            scroll={{ x: 1100, y: '100%' }}
            onRow={(record) => ({
              onClick: () => openIssueInspector(issueReference(record)),
              style: { cursor: 'pointer' },
            })}
            locale={{
              emptyText: (
                <Empty description="No issues match this view" image={Empty.PRESENTED_IMAGE_SIMPLE}>
                  <Button
                    type="primary"
                    onClick={() => {
                      setCreateIssueStatus('BACKLOG');
                      setIsCreateModalOpen(true);
                    }}
                  >
                    New ticket
                  </Button>
                </Empty>
              ),
            }}
          />
        </div>
      ) : (
        <div className="queue-board" role="region" aria-label="Ops Queue board" tabIndex={0}>
          {boardColumns.map((column) => {
            const columnIssues = filteredIssues.filter((issue) => issue.status === column.status);
            const columnPoints = columnIssues.reduce((total, issue) => total + storyPointsForPriority(issue.priority), 0);
            return (
              <section
                className="queue-board-column"
                key={column.key}
                onDragOver={(event) => {
                  event.preventDefault();
                  setDragOverStatus(column.status);
                }}
                onDragLeave={() => setDragOverStatus((current) => current === column.status ? null : current)}
                onDrop={(event) => handleBoardDrop(event.dataTransfer.getData('text/plain'), column.status)}
                style={{
                  minWidth: 284,
                  width: 284,
                  borderTop: `1px solid ${dragOverStatus === column.status ? colors.primary : colors.border}`,
                  background: dragOverStatus === column.status ? 'rgba(219, 80, 172, 0.04)' : 'transparent',
                  transition: 'background 0.16s ease, border-color 0.16s ease',
                }}
              >
                <div className="queue-board-column-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <span style={{ color: colors.textPrimary, fontWeight: 700 }}>{column.title}</span>
                    <span style={{ color: colors.textMuted, fontSize: 12, marginLeft: 8 }}>{columnIssues.length} · {columnPoints} pts</span>
                  </div>
                  <Button
                    type="text"
                    size="small"
                    icon={<FontAwesomeIcon icon={faPlus} />}
                    aria-label={`Create ticket in ${column.title}`}
                    onClick={() => {
                      setCreateIssueStatus(column.status);
                      setIsCreateModalOpen(true);
                    }}
                  />
                </div>
                <div
                  className="queue-board-column-body"
                  role="region"
                  aria-label={`${column.title} tickets`}
                  tabIndex={0}
                  style={{ display: 'flex', flexDirection: 'column', gap: 10, minHeight: 160 }}
                >
                  {columnIssues.length === 0 ? (
                    <Button
                      type="text"
                      icon={<FontAwesomeIcon icon={faPlus} />}
                      onClick={() => {
                        setCreateIssueStatus(column.status);
                        setIsCreateModalOpen(true);
                      }}
                      style={{ color: colors.textMuted, marginTop: 24 }}
                    >
                      Create ticket
                    </Button>
                  ) : columnIssues.map((issue) => (
                    <article
                      key={issue.id}
                      draggable={canEditIssue(issue)}
                      onDragStart={(event) => {
                        event.dataTransfer.setData('text/plain', issue.id);
                        event.dataTransfer.effectAllowed = 'move';
                      }}
                      onClick={() => openIssueInspector(issueReference(issue))}
                      style={{ background: '#09090A', borderRadius: 10, padding: 14, cursor: canEditIssue(issue) ? 'grab' : 'pointer' }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'flex-start', marginBottom: 9 }}>
                        <Tag color={getTypeColor(issue.type)} bordered={false} style={{ margin: 0 }}>{issue.type}</Tag>
                        <Tag color={getPriorityColor(issue.priority)} bordered={false} style={{ margin: 0 }}>{issue.priority}</Tag>
                      </div>
                      <div style={{ color: colors.textPrimary, fontWeight: 650, lineHeight: 1.4, marginBottom: 7 }}>
                        {getIssueDisplayTitle(issue.title, issue.description)}
                      </div>
                      {issue.description && (
                        <div style={{ color: colors.textMuted, fontSize: 12, lineHeight: 1.45, marginBottom: 10, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                          {issue.description}
                        </div>
                      )}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        {issue.assignee ? (
                          <Avatar size="small" src={issue.assignee.photoUrl}>{issue.assignee.name?.charAt(0)}</Avatar>
                        ) : (
                          <span style={{ color: colors.textMuted, fontSize: 12 }}>Unassigned</span>
                        )}
                        <span title="Automatically assigned from priority" style={{ color: colors.textMuted, fontSize: 12 }}>
                          {storyPointsForPriority(issue.priority)} pts
                        </span>
                      </div>
                    </article>
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      )}

      <Drawer
        title="Ticket details"
        open={!!selectedIssueId}
        onClose={closeIssueInspector}
        width="min(760px, 100vw)"
        styles={{
          header: { background: colors.bgBase, borderBottomColor: colors.border },
          body: { background: colors.bgBase },
          footer: { background: colors.bgBase, borderTopColor: colors.border },
        }}
        footer={<div ref={setIssueFooterNode} />}
      >
        {selectedIssueId && (
          <IssueDetail
            issueId={selectedIssueId}
            embedded
            onDeleted={closeIssueInspector}
            footerContainer={issueFooterNode}
            onResolvedReference={replaceIssueReference}
          />
        )}
      </Drawer>
      <CreateIssueModal
        open={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={() => queryClient.invalidateQueries({ queryKey: ['issues'] })}
        onCreated={(issue) => openIssueInspector(issueReference(issue))}
        initialStatus={createIssueStatus}
      />
    </div>
  );
};

export default OpsQueue;
